import { Fragment, useState } from 'react';
import { Box } from '@occc-app/design-system';
import {
  Chip,
  IconButton,
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
  LinkChip,
  StatusChip,
  Table,
  TableBody,
  TableRow,
  Typography,
} from '@occc/ui-design-system';
import { displayAmount } from '@occc-app/lib/currency';
import { pluralize } from '@occc-app/lib/strings';
import { TransactionViewTableCell } from '@occc-app/routes/Transactions/TransactionView/TransactionViewTableCell';

import { TransactionViewItemSavingData } from './types';

const formatPointsAmount = (amount: number): string => {
  const label = `${amount} ${pluralize(amount, 'Point')}`;
  if (amount > 0) return `+${label}`;
  return label;
};

type SavingChipProps = Pick<TransactionViewItemSavingData, 'description' | 'href' | 'isExternal'>;

const SavingChip = ({ description, href, isExternal }: SavingChipProps) => {
  if (isExternal) return <>{description}</>;
  if (href) return <LinkChip label={description} href={href} />;
  return (
    <Chip
      label={
        <Typography component='span' variant='subtitle2'>
          {description}
        </Typography>
      }
      variant='outlined'
    />
  );
};

type SavingAmountProps = Pick<TransactionViewItemSavingData, 'amount' | 'type'>;

const SavingAmount = ({ amount, type }: SavingAmountProps) => (
  <Typography
    color={type === 'points' ? (amount > 0 ? 'primary' : 'error') : 'textSecondary'}
    variant='body2'
  >
    {type === 'points' ? formatPointsAmount(amount) : `-${displayAmount(Math.abs(amount), false)}`}
  </Typography>
);

export const TransactionViewSavingItem = (props: { savings: TransactionViewItemSavingData[] }) => {
  const { savings } = props;

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const toggleExpanded = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Table
      sx={(theme) => ({
        borderCollapse: 'separate',
        borderSpacing: `0 ${theme.spacing(1)}`,
      })}
    >
      <TableBody>
        {savings.map((saving, index) => {
          const { href, description, amount, type, isExternal, count, items } = saving;
          const rowKey = `${href ?? 'saving'}-${type}-${index}`;
          const isGrouped = !!count && count > 1;
          const isExpanded = expandedKeys.has(rowKey);

          return (
            <Fragment key={rowKey}>
              <TableRow hover={false}>
                <TransactionViewTableCell isLeftRadius isHighlighted noBottomBorder>
                  <Box p={1} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isGrouped ? (
                      <IconButton
                        size='small'
                        aria-label={
                          isExpanded ? 'Collapse grouped savings' : 'Expand grouped savings'
                        }
                        aria-expanded={isExpanded}
                        onClick={() => toggleExpanded(rowKey)}
                      >
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    ) : null}
                    <SavingChip description={description} href={href} isExternal={isExternal} />
                    {isGrouped ? <StatusChip label={`×${count}`} /> : null}
                  </Box>
                </TransactionViewTableCell>
                <TransactionViewTableCell align='right' isRightRadius isHighlighted noBottomBorder>
                  <Box p={1}>
                    <SavingAmount amount={amount} type={type} />
                  </Box>
                </TransactionViewTableCell>
              </TableRow>

              {isGrouped && isExpanded
                ? items?.map((item, itemIndex) => (
                    <TableRow hover={false} key={`${rowKey}-item-${itemIndex}`}>
                      <TransactionViewTableCell isLeftRadius noBottomBorder>
                        <Box p={1} pl={6}>
                          <SavingChip
                            description={item.description}
                            href={item.href}
                            isExternal={item.isExternal}
                          />
                        </Box>
                      </TransactionViewTableCell>
                      <TransactionViewTableCell align='right' isRightRadius noBottomBorder>
                        <Box p={1}>
                          <SavingAmount amount={item.amount} type={item.type} />
                        </Box>
                      </TransactionViewTableCell>
                    </TableRow>
                  ))
                : null}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};
